import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import type { PriceImportMappingSpec, PriceImportRun } from './types';
import { priceImportsEndpointIdentifier } from './use-get-price-import-runs-api';

/**
 * Hand-authored column mapping — the fallback for a layout inference cannot
 * crack, and the correction path when it gets one column wrong.
 *
 * Saving re-adapts the whole run, so every row is rebuilt: any per-row decision
 * already made is discarded. Confirm before calling.
 */
type TData = { data: PriceImportRun };
type TError = AxiosError;
type TVariables = { runId: number; spec: PriceImportMappingSpec };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useUpdatePriceImportMappingApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ runId, spec }: TVariables) => {
    try {
      const { data } = await http.put(`${priceImportsEndpointIdentifier}/${runId}/mapping`, spec);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${priceImportsEndpointIdentifier}/mapping`],
    retry: false,
    ...options,
  });
}
